// @file Surface store
import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { ANXIOUS_FACE_EMOJI } from '@@/bits/emoji'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { FontNameToFontId } from '@@/bits/font'
import window from '@@/bits/global'
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { __ } from '@@/bits/intl'
import type { UrlOptions } from '@@/bits/location'
import {
  buildUrlFromPath,
  currentHostname,
  currentHostWithProtocol,
  getHostnameFromUrl,
  loginWithReferrerUrl,
  navigateTo,
  transformUrl,
} from '@@/bits/location'
import { getNotificationPermission, registerForPushNotifications } from '@@/bits/notifications'
import { numberToHumanSize } from '@@/bits/numbers_helper'
import { getVuexStore } from '@@/bits/pinia'
import type { ContentModerationMode } from '@@/enums'
import { LibraryType, PostSize, WallVizEnum } from '@@/enums'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import type { ConfirmationDialogPayload } from '@@/pinia/global_confirmation_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { LibraryUpgradeStep, useLibraryPlansStore } from '@@/pinia/library_plans'
import { UpgradeSource, UpgradeStep, usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { addressListener } from '@@/pinia/plugin/address_listener'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceAdditionalSettingsStore } from '@@/pinia/surface_settings_state'
import { StartingStateLibrary, useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { WallApi } from '@@/surface/api/wall'
import type { Comment, HashId, Id, Post, User, Wall, WallBackground, WallLayoutAttributes, WallViz } from '@@/types'
import { useFreezePadlet } from '@@/vuecomposables/freeze_padlet_store'
import { useSurfaceFetchInitialState } from '@@/vuecomposables/surface_fetch_initial_state'
import { useSurfaceUrlHashShortcuts } from '@@/vuecomposables/surface_url_hash_shortcuts'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import { usePreferredDark } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const SEARCH_BAR_DEFAULT_TOP = 16
const NATIVE_TENANT_ID = 1

export const useSurfaceStore = defineStore('surface', () => {
  // Initialize the addressListener plugin
  window.app?.$pinia.use(addressListener)
  useSurfaceUrlHashShortcuts()

  const surfaceVuexStore = getVuexStore<SurfaceState>()
  const onboardingPanelStore = useSurfaceOnboardingPanelStore()
  const surfaceAIChatStore = useSurfaceAIChatStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const surfaceAdditionalSettingsStore = useSurfaceAdditionalSettingsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const surfacePanelsStore = useSurfacePanelsStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceAccountsStore = useSurfaceAccountsStore()

  // State

  const hasAddPostButtonBeenClicked = ref(false)
  const xExpandedCommentImagePanel = ref(false)
  const headerHeight = ref(128)
  const fileThatExceededSize = ref<File | null>(null)

  const isOnline = ref(true)
  const xWallQuotaExceeded = ref(false)

  // Getters
  const isTemplate = computed<boolean>(() => surfaceVuexStore?.getters.isTemplate)
  const displayAttrs = computed<Partial<Wall>>(() => surfaceVuexStore?.getters.displayAttrs)

  /**
   * Determines if we should show the desktop layout.
   * For desktop devices, we show the desktop layout until the container becomes smaller than tablet portrait size.
   * This means desktop users will see the desktop layout even with narrower windows (between tablet portrait and landscape),
   * as it provides a more appropriate experience for desktop interaction.
   * For non-desktop devices(like tablets), we use a more conservative threshold (tablet landscape) to switch to mobile layout.
   */
  const isMobileLayout = computed<boolean>(() => {
    return (
      !device.app &&
      (device.desktop
        ? useSurfaceContainerSizeStore().isContainerSmallerThanTabletPortrait
        : useSurfaceContainerSizeStore().isContainerSmallerThanTabletLandscape)
    )
  })
  const isDesktopLayout = computed<boolean>(() => {
    return (
      !device.app &&
      (device.desktop
        ? !useSurfaceContainerSizeStore().isContainerSmallerThanTabletPortrait
        : !useSurfaceContainerSizeStore().isContainerSmallerThanTabletLandscape)
    )
  })
  const isScreenshotMode = computed<boolean>(() => surfaceStartingStateStore.startingState.isScreenshotMode)
  const xAuthorInScreenshotMode = computed<boolean>(
    () => surfaceStartingStateStore.startingState.xAuthorInScreenshotMode,
  )
  const xCommentsInScreenshotMode = computed<boolean>(
    () => surfaceStartingStateStore.startingState.xCommentsInScreenshotMode,
  )
  const xReactionsInScreenshotMode = computed<boolean>(
    () => surfaceStartingStateStore.startingState.xReactionsInScreenshotMode,
  )
  const xLogoInScreenshotMode = computed<boolean>(() => surfaceStartingStateStore.startingState.xLogoInScreenshotMode)
  const isGeneratingPDF = computed<boolean>(() => surfaceStartingStateStore.startingState.isGeneratingPDF)
  const isOverlayVisible = computed<boolean>(() => surfaceVuexStore?.getters.isOverlayVisible ?? false)
  const wallId = computed<Id>(() => wallAttributes.value.id)
  const wallHashid = computed<HashId>(() => wallAttributes.value.hashid)
  const wallHashidWithoutPrefix = computed<HashId>(() => removeHashidPrefix(wallHashid.value))
  const background = computed<WallBackground>(() => surfaceVuexStore?.getters.background)
  const postSize = computed<PostSize>(() => displayAttrs.value.post_size ?? PostSize.STANDARD)
  const isPostSizeWide = computed<boolean>(() => postSize.value === PostSize.WIDE)
  const isPostSizeStandard = computed<boolean>(() => postSize.value === PostSize.STANDARD)
  const isWhiteboard = computed<boolean>(() => viz.value === WallVizEnum.Whiteboard)

  // Formats
  const format = computed<WallViz>(() => surfaceVuexStore?.getters.format)
  const isGrid = computed<boolean>(() => format.value === 'grid')
  const isMap = computed<boolean>(() => format.value === 'map')
  const isShelf = computed<boolean>(
    // 17 Apr 2025: "columns" layout is equivalent to (1) viz == "shelf", (2) viz == "columns" or (3) viz == "grid" + isSectioned
    // Once all clients have been updated to not use "columns" as a layout value, we can remove Columns entirely from our codebase.
    () => format.value === 'shelf' || isColumns.value || (isGrid.value && isSectioned.value),
  )
  const isStream = computed<boolean>(() => format.value === 'stream')
  const isColumns = computed<boolean>(() => format.value === 'columns')
  const isSectioned = computed<boolean>(() => surfaceVuexStore?.getters.isSectioned)
  const isTimeline = computed<boolean>(() => format.value === 'timeline' || format.value === 'timeline_v2')
  const isTimelineV1 = computed<boolean>(() => format.value === 'timeline')
  const isTimelineV2 = computed<boolean>(() => format.value === 'timeline_v2')
  const isCanvas = computed<boolean>(() => format.value === 'free')
  const isMatrix = computed<boolean>(() => format.value === 'matrix')
  const isTable = computed<boolean>(() => format.value === 'table')

  const isWishContentScrollable = computed<boolean>(() => isTimeline.value || isMap.value)

  const isCustomBackground = computed<boolean>(() => surfaceVuexStore?.getters.background.is_custom)
  const isBackgroundBlurred = computed<boolean>(() => surfaceVuexStore?.getters.isBackgroundBlurred)
  const isTitleBarTransparent = computed<boolean>(() => surfaceVuexStore?.getters.isTitleBarTransparent)
  const backgroundColor = computed(
    () =>
      displayAttrs.value.background?.primary_color_as_rgb ?? displayAttrs.value.background?.dominant_color_as_rgb ?? '',
  )
  const backgroundLuminance = computed<'light' | 'dark'>(() => displayAttrs.value.background?.luminance_name ?? 'light')
  const colorScheme = computed<'light' | 'dark'>(() =>
    displayAttrs.value?.color_scheme === 'light' ? 'light' : 'dark',
  )
  const skinAccentColorAsRGB = computed(() => displayAttrs.value.background?.accent_color_as_rgb ?? '')
  const skinPrimaryColorAsRGB = computed(() => displayAttrs.value.background?.primary_color_as_rgb ?? '')
  const skinPrimaryVariantColorAsRGB = computed(() => surfaceVuexStore?.getters.skinPrimaryVariantColorAsRGB)
  const skinPrimaryTextColor = computed<'white' | 'black' | undefined>(
    () => displayAttrs.value.background?.primary_text_color ?? undefined,
  )
  const skinAccentTextColor = computed<'white' | 'black' | undefined>(
    () => displayAttrs.value.background?.accent_text_color ?? undefined,
  )
  const isSkinColorAvailable = computed<boolean>(() => {
    return (
      skinPrimaryTextColor.value != null &&
      skinPrimaryVariantColorAsRGB.value != null &&
      skinAccentColorAsRGB.value != null &&
      skinAccentColorAsRGB.value != null
    )
  })
  const useSkinColors = computed(() => skinAccentColorAsRGB.value != null && skinAccentTextColor.value != null)
  const onboardingDemoButtonStyle = computed(() => {
    if (surfaceSettingsStore.isUsingSolidPurpleLightWallpaper) {
      return { backgroundColor: '#AE56E4', color: '#FFFFFF' }
    }

    if (useSkinColors.value) {
      return {
        backgroundColor: skinAccentColorAsRGB.value,
        color: skinAccentTextColor.value,
      }
    }

    return {}
  })
  const buttonDarkMode = computed<boolean>(() => {
    if (!isSkinColorAvailable.value) {
      return usePreferredDark().value
    }
    // skinPrimaryTextColor is the color for text/button icon when shown on top of the skin's primary color
    // darkMode means dark background and light icons
    // => buttonDarkMode  should be true when we want white text color
    return skinPrimaryTextColor.value === 'white'
  })
  const isReadOnly = computed(() => surfaceStartingStateStore.startingState.isReadOnly)
  const isSimplifiedView = computed(() => surfaceStartingStateStore.startingState.isSimplifiedView)
  const isEmbedded = computed<boolean>(() => surfaceStartingStateStore.startingState.isEmbedded)
  const isLtiEmbedded = computed<boolean>(() => surfaceStartingStateStore.startingState.isLtiEmbedded)
  const isWhiteboardEmbedPlayMode = computed<boolean>(
    () => surfaceStartingStateStore.startingState.isWhiteboardEmbedPlayMode,
  )
  const isGoogleClassroomEmbedded = computed<boolean>(
    () => surfaceStartingStateStore.startingState.isGoogleClassroomEmbedded,
  )
  const googleClassroomAuthorIdFilter = computed<string>(
    () => surfaceStartingStateStore.startingState.googleClassroomAuthorIdFilter,
  )
  const isMicrosoftTeamsEmbedded = computed<boolean>(
    () => surfaceStartingStateStore.startingState.isMicrosoftTeamsEmbedded,
  )
  const isNonLtiEmbedded = computed(() => isEmbedded.value && !isLtiEmbedded.value)
  const defaultPdfPageSize = computed<string>(() => surfaceStartingStateStore.startingState.defaultPdfPageSize)
  const dir = computed<'ltr' | 'rtl'>(() => surfaceVuexStore?.getters.dir)
  const fonts = computed(() => {
    // in SettingsV2, we want the fonts to be in a different order
    const fontIdOrder = [
      FontNameToFontId.ComicNeueAngular,
      FontNameToFontId.Lora,
      FontNameToFontId.AverageMono,
      FontNameToFontId.Inter,
    ]
    const resultFonts = fontIdOrder.map((id) =>
      surfaceStartingStateStore.startingState.constants?.allFonts?.find((font) => font.id === id),
    )
    return resultFonts
  })
  const whiteboardFonts = computed(() => {
    const fontIdOrder = [
      FontNameToFontId.ShantellSans,
      FontNameToFontId.Lora,
      FontNameToFontId.CourierPrime,
      FontNameToFontId.Inter,
    ]
    const resultFonts = fontIdOrder.map((id) =>
      surfaceStartingStateStore.startingState.constants?.allFonts?.find((font) => font.id === id),
    )
    return resultFonts
  })
  const fontId = computed(() => surfaceVuexStore?.getters.fontId)
  const viz = computed<WallViz>(() => surfaceVuexStore?.getters.viz)
  const newPostLocation = computed<'top' | 'bottom'>(() => displayAttrs.value.new_wish_loc ?? 'bottom')
  const address = computed<string>(() => surfaceVuexStore?.getters.address)
  const builder = computed<User>(() => surfaceVuexStore?.getters.builder)
  const rawTitle = computed<string>(() => surfaceVuexStore?.getters.title)
  const title = computed<string>(() => {
    if (surfaceTranslationsStore.isViewingTranslatedPadlet && surfaceTranslationsStore.translatedWall?.title != null) {
      return surfaceTranslationsStore.translatedWall.title
    }
    return rawTitle.value
  })
  const rawDescription = computed<string | undefined>(() => surfaceVuexStore?.getters.description)
  const description = computed<string | undefined>(() => {
    if (
      surfaceTranslationsStore.isViewingTranslatedPadlet &&
      surfaceTranslationsStore.translatedWall?.description != null
    ) {
      return surfaceTranslationsStore.translatedWall.description
    }
    return rawDescription.value
  })
  const portrait = computed<string>(() => surfaceVuexStore?.getters.portrait)
  const wallAttributes = computed<Wall>(() => surfaceVuexStore?.state.wall)
  const wallCreatedAt = computed<string>(() => wallAttributes.value.created_at)
  const wallUpdatedAt = computed<string>(() => wallAttributes.value.updated_at)
  const mapThemeId = computed<number>(() => surfaceVuexStore?.getters.themeId)
  const contentModeration = computed<ContentModerationMode>(() => wallAttributes.value.content_moderation)
  const filterProfanity = computed<boolean>(() => displayAttrs.value.filter_profanity ?? false)
  const domainName = computed<string>(() => surfaceVuexStore?.getters.domainName)
  const namespace = computed<string>(() => surfaceVuexStore?.getters.namespace)
  const publicKey = computed<string>(() => surfaceVuexStore?.getters.publicKey)
  const permalink = computed<string>(() => surfaceVuexStore?.getters.name)
  const links = computed<Record<string, string>>(() => surfaceVuexStore?.getters.links)
  const coverPostId = computed<Id | null>(() => surfaceVuexStore?.getters.coverPostId)
  const originalBackground = computed<WallBackground>(() => wallAttributes.value.background)
  const xAuthor = computed<boolean>(() => {
    if (isScreenshotMode.value && !xAuthorInScreenshotMode.value) return false
    return surfaceVuexStore?.getters.xAuthor
  })
  const xCommentsUnderPosts = computed<boolean>(() => {
    if (isScreenshotMode.value && !xCommentsInScreenshotMode.value) return false
    return !isMap.value && !isTable.value && !isCanvas.value && !isTimeline.value
  })
  const uploadLimit = computed(() => wallAttributes.value?.upload_limit)
  const whiteboardFrameLimit = computed<number>(() => wallAttributes.value?.whiteboard_frame_limit ?? 20)
  const isBuilderCurrentUser = computed((): boolean => user.value.id === builder.value.id)

  const canUseSections = computed<boolean>(() => surfaceStartingStateStore.sectionableLayouts.includes(viz.value))
  const canHaveConnections = computed<boolean>(() => isCanvas.value || isMap.value)
  const isUsedInEducationalContext = computed<boolean>(
    () => surfaceStartingStateStore.startingState.isUsedInEducationalContext ?? false,
  )
  const user = computed<User>(() => surfaceVuexStore?.getters.user)
  const xFilterProfanitySetting = computed<boolean>(
    () => surfaceStartingStateStore.startingState.xFilterProfanitySetting ?? false,
  )
  const allLayouts = computed(() => surfaceStartingStateStore.allLayouts)
  const allLayoutsByGroup = computed<{
    simple: WallLayoutAttributes[]
    advanced: WallLayoutAttributes[]
  }>(() => surfaceStartingStateStore.allLayoutsByGroup)

  const isSubmissionRequest = computed<boolean>(() => surfaceStartingStateStore.startingState.isSubmissionRequest)
  const isSlideshow = computed<boolean>(() => surfaceStartingStateStore.startingState.isSlideshow)
  const blockedContentProviders = computed<Set<string>>(
    () => surfaceStartingStateStore.startingState.blockedContentProviders,
  )
  const loginAndRedirectToWallUrl = computed<string>(
    () => surfaceStartingStateStore.startingState.loginAndRedirectToWallUrl,
  )
  const loginAndRedirectUrlExpireDuration = computed<number>(
    () => surfaceStartingStateStore.startingState.loginAndRedirectUrlExpireDuration,
  )
  const isFirstView = computed<boolean>(() => surfaceStartingStateStore.startingState.isFirstView)
  const magicWallType = computed<string | null>(() => surfaceStartingStateStore.startingState.magicWallType)
  const isMagicWall = computed<boolean>(() => magicWallType.value != null)

  const nativePostEdit = computed<boolean>(() => surfaceStartingStateStore.startingState.nativePostEdit)
  const xNewComposerModalPanel = computed<boolean>(() => {
    // We do not show the composer panel for mobile app or isReadOnly
    if (nativePostEdit.value) return false
    if (isReadOnly.value) return false
    if (useSurfaceDraftsStore().draftCids.length > 0) {
      // Always show the composer modal panel if there are drafts.
      // Allow users to see their unpublished drafts, even if their write permissions have been revoked.
      return true
    }
    if (surfacePermissionsStore.canIWrite) return true
    return false
  })
  const xSettingsPanel = computed<boolean>(() => surfaceVuexStore?.getters.xSettingsPanel)
  const isSubscriptionPaused = computed<boolean>(
    () => surfaceStartingStateStore.startingState.isSubscriptionPaused ?? false,
  )
  const canInviteStudents = computed<boolean>(() => surfaceAdditionalSettingsStore.canInviteStudents)
  const isWallTransferable = computed<boolean>(() => surfaceAdditionalSettingsStore.isWallTransferable)
  const canAddClassToWall = computed<boolean>(() => surfaceStartingStateStore.startingState.canAddClassToWall)
  const canUseLibraryAsTeacher = computed<boolean>(() => surfaceStartingStateStore.startingState.canUseLibraryAsTeacher)
  const postHeightAllWidths = computed<Record<string, Record<string, number>>>(
    () => surfaceStartingStateStore.startingState?.postHeights,
  )
  const libraryDashboardUrl = computed<string | undefined>(() => library.value?.dashboard_url)
  const advertizedUploadLimit = computed<number>(() => wallAttributes.value.advertized_upload_limit)

  // #region search bar
  const xSearchBar = ref(false)
  const isSearchBarInEditMode = ref(false)
  const searchBarTop = ref(SEARCH_BAR_DEFAULT_TOP)

  /**
   * Open the search bar.
   * To use where updating xSearchBar directly is not possible (e.g. in a Vue 2 component).
   */
  function openSearchBar(): void {
    xSearchBar.value = true
  }

  function updateSearchBarTop(top: number): void {
    searchBarTop.value = top
  }

  function resetSearchBarTop() {
    searchBarTop.value = SEARCH_BAR_DEFAULT_TOP
  }
  // #endregion

  function refresh({ isAutoRefresh }: { isAutoRefresh?: boolean }): void {
    void surfaceVuexStore?.dispatch('refresh', { isAutoRefresh })
  }

  function changeConnectivity(isUserOnline: boolean): void {
    isOnline.value = isUserOnline
  }

  function triggerOverFileSizeAlertDialog(): void {
    useGlobalAlertDialogStore().openAlertDialog({
      iconSrc: ANXIOUS_FACE_EMOJI.iconSrc,
      title: __('File too large!'),
      body: __('This padlet has a file size limit of %{num}.', {
        num: numberToHumanSize(advertizedUploadLimit.value, { precision: 2, strip_insignificant_zeros: true }),
      }),
      xShadow: true,
    })
  }

  function showOverFileSizeModal({ file }: { file: File }): void {
    useSurfacePostsStore().rejectPostsWithFileSizeExceeded({ file })
    fileThatExceededSize.value = file

    if (isLibraryWall.value) {
      if (surfaceAccountsStore.currentLibrary == null) {
        captureMessage('[showOverFileSizeModal] library is undefined for library wall', {
          context: {
            wall: wallAttributes.value,
          },
        })
        return
      }
      const MAX_UPLOAD_BYTES = 1200000000 // 1.2GB
      if (!amILibraryOwner.value) {
        triggerOverFileSizeAlertDialog()
      } else {
        if (file.size > MAX_UPLOAD_BYTES) {
          triggerOverFileSizeAlertDialog()
        } else {
          void useLibraryPlansStore().fetchPlansAndShowUpgradeModal({
            upgradeStep: LibraryUpgradeStep.ChooseTierFileSizeQuota,
            library: surfaceAccountsStore.currentLibrary,
          })
        }
      }
    } else {
      const MAX_UPLOAD_BYTES = 600000000 // 600MB
      if (!surfacePermissionsStore.amIOwner) {
        triggerOverFileSizeAlertDialog()
      } else {
        if (file.size > MAX_UPLOAD_BYTES) {
          triggerOverFileSizeAlertDialog()
        } else {
          usePersonalPlansStore().showPlanUpgradeModalCheckingScheduledChanges({
            step: UpgradeStep.ChooseTierFileSizeQuota,
            membershipTier: user.value.membership_tier,
            paymentSchedule: user.value.period,
            upgradeSource: UpgradeSource.FileSizeLimit,
          })
        }
      }
    }
  }

  function showPromptToLoginDialog(
    payload: Partial<ConfirmationDialogPayload> & { urlTransformParams?: UrlOptions },
  ): void {
    const defaultPromptToLoginDialogPayload = {
      title: '',
      body: __('Please log in to your Padlet account or sign up for one.'),
      confirmButtonText: __('Log in'),
      cancelButtonText: __('Nevermind'),
      afterConfirmActions: [
        async () => {
          let redirectUrl = wallAttributes.value.links?.permanent
          if (payload.urlTransformParams != null && redirectUrl) {
            redirectUrl = transformUrl(redirectUrl, payload.urlTransformParams)
          }

          if (redirectUrl) {
            navigateTo(loginWithReferrerUrl(redirectUrl))
          }
        },
      ],
    }
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...defaultPromptToLoginDialogPayload,
      ...payload,
    })
  }

  // Header
  const xHeader = computed(() => !device.app || !appCan('useNativeHeader'))
  const headerColor = computed<string>(() => {
    if (isMap.value) {
      if (backgroundColor.value == null || backgroundColor.value === '') {
        return colorScheme.value === 'light' ? '#FFFFFF' : '#000000'
      }
      return backgroundColor.value
    }
    if (isTitleBarTransparent.value) {
      return 'transparent'
    }
    return backgroundColor.value
  })
  function resizeHeader({ height }: { height: number }): void {
    headerHeight.value = height
  }

  // #region libraries
  const library = computed<StartingStateLibrary>(() => surfaceStartingStateStore.startingState.library)
  const libraryId = computed<number | undefined>(
    () => library.value?.id ?? wallAttributes.value?.library_id ?? undefined,
  )
  const isLibraryWall = computed<boolean>(() => libraryId.value != null)
  const libraryName = computed<string | undefined>(() => library.value?.name)
  const libraryHashid = computed<HashId | undefined>(() => library.value?.hashid)
  const isClassroomLibrary = computed<boolean>(() => library.value?.libraryType === LibraryType.Classroom)
  const isSchoolLibrary = computed<boolean>(() => library.value?.libraryType === LibraryType.School)
  const isClassroomOrSchoolLibrary = computed<boolean>(() => isClassroomLibrary.value || isSchoolLibrary.value)
  const canUseLibraryAsMember = computed<boolean>(() => surfaceStartingStateStore.startingState.canUseLibraryAsMember)
  const amILibraryOwner = computed<boolean>(() => surfaceStartingStateStore.startingState.amILibraryOwner)
  // #endregion

  // #region tenants
  const isOrgWall = computed<boolean>(() => {
    if (wallAttributes.value?.tenant_id) {
      return wallAttributes.value?.tenant_id > NATIVE_TENANT_ID
    }
    return false
  })
  const orgName = computed<string>(() => surfaceStartingStateStore.startingState.tenant.name)
  const tenantId = computed<number | undefined>(() => wallAttributes.value?.tenant_id)

  const isNativeTenant = computed<boolean>(() => {
    return wallAttributes.value?.tenant_id === NATIVE_TENANT_ID
  })
  // #endregion

  // frozen state
  const { isFrozen, toggleFreezePadlet, handlePadletFrozen, handlePadletUnfrozen } = useFreezePadlet(
    wallAttributes,
    surfaceVuexStore,
  )

  // padlets in a personal library that's over quota are always frozen
  // similar logic to wall.rb#is_frozen?
  const isFrozenDueToOverQuota = computed(() => {
    // Whiteboards created before 1 Oct 2024 are not frozen
    if (!isLibraryWall.value && isWhiteboard.value && Date.now() < Date.UTC(2024, 9, 1)) return false
    return isNativeTenant.value && !isLibraryWall.value && builder.value.days_before_freeze === 0
  })

  // templates
  async function markWallAsTemplate(): Promise<void> {
    setWallAsTemplate()
    try {
      await WallApi.markAsTemplate(wallId.value, true)
    } catch {
      useGlobalSnackbarStore().genericFetchError()
      unsetWallAsTemplate()
    }
  }

  function setWallAsTemplate(): void {
    updateWall({ is_template: true })
  }

  async function unmarkWallAsTemplate(): Promise<void> {
    unsetWallAsTemplate()
    try {
      await WallApi.markAsTemplate(wallId.value, false)
    } catch {
      useGlobalSnackbarStore().genericFetchError()
      setWallAsTemplate()
    }
  }

  function unsetWallAsTemplate(): void {
    updateWall({ is_template: false })
  }

  // Side panel
  const hasSidePanelOutsideSurface = computed(
    (): boolean =>
      onboardingPanelStore.xOnboardingPanel ||
      surfacePanelsStore.xSurfaceGradingPanel ||
      surfaceAIChatStore.xSurfaceAIChatPanel,
  )

  const isSidePanelRounded = computed(
    (): boolean => onboardingPanelStore.xOnboardingPanel || surfacePanelsStore.xSurfaceGradingPanel,
  )

  // Transfer Wall
  const xTransferWallDialog = ref<boolean>(false)

  function openTransferWallDialog(): void {
    xTransferWallDialog.value = true
  }

  function closeTransferWallDialog(): void {
    xTransferWallDialog.value = false
  }

  // #region Push notifications permissions
  const xPermitNotification = ref(false)
  const hasShownRegisterNotificationsSnackbar = ref(false)

  async function registerForPushNotificationsForUser(): Promise<void> {
    void registerForPushNotifications(user.value).catch((e) => {
      console.warn(`No push`, e)
      xPermitNotification.value = true
    })
  }

  async function checkAndSubscribeToPushNotifications(): Promise<void> {
    const permission = await getNotificationPermission()
    if (permission === 'granted') {
      await registerForPushNotificationsForUser()
    } else if (permission === 'default') {
      if (!hasShownRegisterNotificationsSnackbar.value) {
        hasShownRegisterNotificationsSnackbar.value = true
        useGlobalSnackbarStore().setSnackbar({
          message: __('Turn on notifications for this browser?'),
          actionText: __('OK'),
          actionTextActions: [async () => await registerForPushNotificationsForUser()],
          timeout: 6000,
        })
      }
    }
    // Respect the user's decision if denied
    // else if (permission === 'denied') {
    //   // Displays unblocking instructions
    //   dispatch('registerForPushNotifications')
    // }
  }

  function hidePermitNotification(): void {
    xPermitNotification.value = false
  }
  // #endregion

  function showCommentsPanel(postId): void {
    void surfaceVuexStore?.dispatch('showCommentsPanel', { postId })
  }

  function hideWallQuotaExceeded(): void {
    void surfaceVuexStore?.dispatch('hideWallQuotaExceeded')
  }

  function updateUser(user: User): void {
    void surfaceVuexStore?.dispatch('updateUser', user)
  }

  function setAddPostButtonClickStatus(status: boolean): void {
    hasAddPostButtonBeenClicked.value = status
  }

  async function refetchStartingStateAndCreateNewBrahmsConnection(): Promise<void> {
    const surfaceStartingStateStore = useSurfaceStartingStateStore()
    await surfaceStartingStateStore.fetchStartingState()
    useSurfaceFetchInitialState().fetchInitialState()
    void surfaceVuexStore?.dispatch('refreshPadletStartingState', surfaceStartingStateStore.startingState)
    void surfaceVuexStore?.dispatch('realtime/createNewBrahmsConnectionForUser', {
      userId: surfaceStartingStateStore.startingState.user.id,
      brahmsToken: surfaceStartingStateStore.startingState.brahmsToken,
    })
  }

  async function approveAllContributions({
    wallHashid,
    unapprovedPosts,
    unapprovedComments,
  }: {
    wallHashid: HashId
    unapprovedPosts: Post[]
    unapprovedComments: Comment[]
  }): Promise<void> {
    try {
      await WallApi.approveAll({ wallHashid, approvalCutoffTimestamp: new Date().toISOString() })
      const postEntitiesByCid = surfacePostsStore.postEntitiesById
      unapprovedPosts.forEach((post) => {
        if (post?.id == null) return
        const postCid = postEntitiesByCid[post.id].cid
        void surfaceVuexStore?.commit('post/APPROVE_POST', { postCid })
      })
      unapprovedComments.forEach((comment) => {
        void surfaceVuexStore?.commit('comment/UPDATE_COMMENT', {
          id: comment.id,
          obj: { is_awaiting_approval: false },
        })
      })
    } catch {
      void globalSnackbarStore.genericFetchError()
    }
  }

  function updateWall(obj: Partial<Wall>): void {
    void surfaceVuexStore?.dispatch('updateWall', obj)
  }

  const homeUrl = computed<string>(() => {
    if (isAppUsing('defaultDashboardLibraryNavigation')) {
      // Always redirect to the dashboard since the backend handles the dashboard navigation logic
      const dashboardUrl = buildUrlFromPath('dashboard')
      return transformUrl(dashboardUrl)
    }
    const referrer = document.referrer
    // If there is a referrer and the referrer is an internal link
    // we return the referrer as the home url
    if (referrer !== '' && getHostnameFromUrl(referrer) === currentHostname() && referrer.includes('/dashboard')) {
      return referrer
    }
    if (!isLibraryWall.value && isBuilderCurrentUser.value) {
      const dashboardUrl = buildUrlFromPath('dashboard')
      return transformUrl(dashboardUrl, {
        searchParams: {
          filter: 'made',
          mobile_page: 'Collection',
        },
      })
    }
    if (isLibraryWall.value && library.value.dashboard_url && canUseLibraryAsMember.value) {
      return transformUrl(library.value.dashboard_url, {
        searchParams: {
          filter: 'all',
          mobile_page: 'Collection',
        },
      })
    }
    return currentHostWithProtocol()
  })

  // one-time migration of reactions -> custom properties/emoji reactions completed.
  // https://whimsical.com/emoji-reactions-v2-migrating-reactions-to-custom-fields-E7m5goVijTFMdZ5Cmcpbyz
  const reactionsMigrated = computed<boolean>(() => surfaceStartingStateStore.startingState.wall.reactions_migrated)

  return {
    // Search bar
    xSearchBar,
    isSearchBarInEditMode,
    searchBarTop,
    openSearchBar,
    updateSearchBarTop,
    resetSearchBarTop,

    xTransferWallDialog,
    hasSidePanelOutsideSurface,
    isSidePanelRounded,
    hasAddPostButtonBeenClicked,
    xExpandedCommentImagePanel,
    fileThatExceededSize,

    // Getters
    isTemplate,
    isMobileLayout,
    isDesktopLayout,
    isScreenshotMode,
    xCommentsInScreenshotMode,
    xReactionsInScreenshotMode,
    xLogoInScreenshotMode,
    isGeneratingPDF,
    isOverlayVisible,
    wallId,
    wallHashid,
    wallHashidWithoutPrefix,
    xHeader,
    headerHeight,
    headerColor,
    background,
    googleClassroomAuthorIdFilter,

    // Formats
    format,
    isGrid,
    isMap,
    isShelf,
    isSectioned,
    isStream,
    isTimeline,
    isTimelineV1,
    isTimelineV2,
    isCanvas,
    isMatrix,
    isTable,

    isWishContentScrollable,

    isWhiteboard,
    isCustomBackground,
    isBackgroundBlurred,
    isTitleBarTransparent,
    backgroundColor,
    skinAccentColorAsRGB,
    skinPrimaryColorAsRGB,
    skinPrimaryVariantColorAsRGB,
    skinPrimaryTextColor,
    skinAccentTextColor,
    isSkinColorAvailable,
    buttonDarkMode,
    backgroundLuminance,
    colorScheme,
    isReadOnly,
    isSimplifiedView,
    isFrozen,
    isFrozenDueToOverQuota,
    isEmbedded,
    isLtiEmbedded,
    isGoogleClassroomEmbedded,
    isMicrosoftTeamsEmbedded,
    isWhiteboardEmbedPlayMode,
    isNonLtiEmbedded,
    defaultPdfPageSize,
    dir,
    fonts,
    whiteboardFonts,
    fontId,
    viz,
    newPostLocation,
    address,
    builder,
    title,
    rawTitle,
    description,
    rawDescription,
    portrait,
    wallAttributes,
    mapThemeId,
    filterProfanity,
    domainName,
    namespace,
    publicKey,
    permalink,
    links,
    canUseSections,
    canHaveConnections,
    isOnline,
    coverPostId,
    originalBackground,
    xAuthor,
    xCommentsUnderPosts,
    isUsedInEducationalContext,
    wallCreatedAt,
    wallUpdatedAt,
    user,
    xFilterProfanitySetting,
    allLayouts,
    allLayoutsByGroup,
    postSize,
    isPostSizeWide,
    isPostSizeStandard,

    // Submission request
    isSubmissionRequest,

    isSlideshow,
    blockedContentProviders,
    homeUrl,
    uploadLimit,
    library,
    isLibraryWall,
    isOrgWall,
    libraryName,
    isClassroomLibrary,
    isSchoolLibrary,
    isClassroomOrSchoolLibrary,
    orgName,
    tenantId,
    libraryId,
    libraryHashid,
    loginAndRedirectToWallUrl,
    loginAndRedirectUrlExpireDuration,
    xWallQuotaExceeded,
    isFirstView,
    isMagicWall,
    magicWallType,
    xNewComposerModalPanel,
    xSettingsPanel,
    isSubscriptionPaused,
    isNativeTenant,
    contentModeration,
    whiteboardFrameLimit,
    isBuilderCurrentUser,
    onboardingDemoButtonStyle,
    canInviteStudents,
    isWallTransferable,
    canAddClassToWall,
    canUseLibraryAsTeacher,
    postHeightAllWidths,
    libraryDashboardUrl,

    // Actions
    checkAndSubscribeToPushNotifications,
    refresh,
    changeConnectivity,
    handlePadletFrozen,
    handlePadletUnfrozen,
    showOverFileSizeModal,
    showPromptToLoginDialog,
    toggleFreezePadlet,
    markWallAsTemplate,
    setWallAsTemplate,
    unmarkWallAsTemplate,
    unsetWallAsTemplate,
    openTransferWallDialog,
    closeTransferWallDialog,
    resizeHeader,
    showCommentsPanel,
    hideWallQuotaExceeded,
    updateUser,
    setAddPostButtonClickStatus,
    refetchStartingStateAndCreateNewBrahmsConnection,
    approveAllContributions,
    updateWall,

    // Push notifications permissions
    xPermitNotification,
    hidePermitNotification,

    reactionsMigrated,
  }
})
